import { Box, Typography } from "@mui/material"
import React, { useCallback, useContext, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { customPalette } from "../../../../../../../../../../theme"
import { CustomBottomNavigation } from "../../../../../../../../../components/buttons/navigate-button/CustomBottomNavigation"
import { IconInbox } from "../../../../../../../../../components/custom-icons/icon-inbox"
import { pagesUrl } from "../../../../../../../../../core/appConstants"
import { AppSideBarContext } from "../../../../../../../../../core/context/AppSideBarContext/AppSideBarContext"
import { BSVariantContext } from "../../../../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { AppNavBarContext, NavbarTypeEnum } from "../../../../../../../../../core/context/nav-bar/AppNavBarContext"
import { CoverImgProjectContext } from "../../../../../../../../../core/context/project/CoverImgProjectContext"
import { ProjectContext } from "../../../../../../../../../core/context/project/ProjectContext"
import { BSBuildingMorphologyEnum } from "../../../../../../../../../core/enum/bsBuildingMorphologyEnum"
import { useForm } from "../../../../../../../../../core/hooks/form/use-form"
import { nonZero, required, ValidationRule } from "../../../../../../../../../core/hooks/form/validation"
import { resolveUrl } from "../../../../../../../../../core/services/http-service"
import {
  bsBuildingMorphologyDtoToForm,
  BSBuildingMorphologyForm,
  bsBuildingMorphologyFormToCreationDto,
  bsBuildingMorphologyFormToUpdateDto,
  IBSBuildingMorphologyForm,
} from "../../../../tabs/BSBuildingTabs/BSBuildingMorphologyTab/components/BSBuildingMorphologyForm"
import { BSBuildingMorphologyContext } from "../../../../tabs/BSBuildingTabs/BSBuildingMorphologyTab/context/BSBuildingMorphologyContext"
import { BSBuildingContext } from "../../../components/context/BSBuildingContext"

export function BSBuildingWithoutBimModel(): React.JSX.Element {
  const navigate = useNavigate()

  const { setShowAppSide, setFullScreen } = useContext(AppSideBarContext)
  const { setTypeNavBar, setImageFile, setIcon, setTitle, setPreviousUrl, setStepHeader } = useContext(AppNavBarContext)
  const { file } = useContext(CoverImgProjectContext)
  const { project } = useContext(ProjectContext)
  const { selectedVariant } = useContext(BSVariantContext)
  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const { bsBuildingMorphology, createNewBSBuildingMorphology, updateBuildingMorphology } =
    useContext(BSBuildingMorphologyContext)

  useEffect(() => {
    setTypeNavBar(NavbarTypeEnum.BUILDING_HEADER)
    setImageFile(file)
    setIcon(<IconInbox sx={{ color: customPalette.black, fontWeight: "2px" }} />)
    setTitle(selectedVariant?.name ?? "")
    setPreviousUrl(resolveUrl(pagesUrl.VARIANT_DETAILS, [project?.id, selectedVariant?.id]))
    setFullScreen(false)
    setStepHeader(1)
  }, [
    file,
    project?.id,
    selectedVariant?.id,
    selectedVariant?.name,
    setFullScreen,
    setIcon,
    setImageFile,
    setPreviousUrl,
    setStepHeader,
    setTitle,
    setTypeNavBar,
  ])

  useEffect(() => {
    setShowAppSide(false)
  }, [setShowAppSide])

  const submit: (form: IBSBuildingMorphologyForm) => Promise<any> = useCallback(
    (form) => {
      if (project?.id && selectedVariant?.id && selectedBSBuilding?.id && !bsBuildingMorphology?.id) {
        return createNewBSBuildingMorphology(bsBuildingMorphologyFormToCreationDto(form, selectedBSBuilding.id)).then(() => {
          navigate(resolveUrl(pagesUrl.BUILDING_PARKING, [project.id, selectedVariant.id, selectedBSBuilding.id, "new"]))
        })
      } else if (project?.id && selectedVariant?.id && selectedBSBuilding?.id && bsBuildingMorphology?.id) {
        return updateBuildingMorphology(
          bsBuildingMorphologyFormToUpdateDto(form, selectedBSBuilding.id, bsBuildingMorphology.id)
        ).then(() => {
          navigate(resolveUrl(pagesUrl.BUILDING_PARKING, [project.id, selectedVariant.id, selectedBSBuilding.id, "new"]))
        })
      }
      return Promise.resolve()
    },
    [
      bsBuildingMorphology?.id,
      createNewBSBuildingMorphology,
      navigate,
      project?.id,
      selectedBSBuilding?.id,
      selectedVariant?.id,
      updateBuildingMorphology,
    ]
  )

  const { form, errors, handleChange, handleSubmit, isSubmitting, setRules } = useForm(
    bsBuildingMorphology,
    bsBuildingMorphologyDtoToForm,
    [],
    submit
  )

  useEffect(() => {
    const rules: ValidationRule<IBSBuildingMorphologyForm>[] = []
    if (form.shape === BSBuildingMorphologyEnum.L) {
      rules.push(
        ...[
          required("shape"),
          required("dimensionL1"),
          required("dimensionL2"),
          required("dimensionL3"),
          required("dimensionL4"),
          nonZero("dimensionL1"),
          nonZero("dimensionL2"),
          nonZero("dimensionL3"),
          nonZero("dimensionL4"),
        ]
      )
    }

    if (form.shape === BSBuildingMorphologyEnum.RECTANGLE) {
      rules.push(
        ...[
          required("shape"),
          required("dimensionRectangle1"),
          required("dimensionRectangle2"),
          nonZero("dimensionRectangle1"),
          nonZero("dimensionRectangle2"),
        ]
      )

      if (form.murRideauVitrage) {
        rules.push(...[required("ratioMursRideauxVitrage"), nonZero("ratioMursRideauxVitrage")])
      }

      if (form.verrieresToiture) {
        rules.push(...[required("ratioVerrieresToiture"), nonZero("ratioVerrieresToiture")])
      }
    }
    setRules(rules)
  }, [form.murRideauVitrage, form.shape, form.verrieresToiture, setRules])

  return (
    <Box
      component="form"
      id="create-morphology-form"
      onSubmit={handleSubmit}
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      gap={2}>
      <Typography variant="h5" fontWeight={600} p={3}>
        Renseigner la morphologie principale
      </Typography>
      <Box width="70%">
        <BSBuildingMorphologyForm form={form} errors={errors} handleChange={handleChange} />
      </Box>

      <CustomBottomNavigation actionLabel="Continuer" isSubmitting={isSubmitting} formId="create-morphology-form" />
    </Box>
  )
}
